import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import Header from "@/components/layout/header";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Plus,
  Edit,
  Trash2,
  AlertCircle,
  ArrowUp,
  ArrowDown,
  ImageOff,
} from "lucide-react";
import FeedAdminGuard from "@/components/feed/FeedAdminGuard";
import ImageUploadField from "@/components/feed/ImageUploadField";
import {
  useFeedStickers,
  useCreateFeedSticker,
  useUpdateFeedSticker,
  useDeleteFeedSticker,
} from "@/hooks/useFeedStickers";
import type {
  FeedStickerResponse,
  FeedStickerRequest,
} from "@/types/generated/cms.generated";

const STICKER_UPLOAD_ENDPOINT = "/feed-stickers/upload-url";

const stickerFormSchema = z.object({
  name: z.string().min(2, "Name must be at least 2 characters"),
  blobUrl: z.string().min(1, "An image is required"),
  sortOrder: z.number().default(0),
  isActive: z.boolean().default(true),
});

type StickerFormData = z.infer<typeof stickerFormSchema>;

interface StickerModalProps {
  open: boolean;
  onClose: () => void;
  sticker?: FeedStickerResponse;
  nextSortOrder: number;
}

function StickerModal({
  open,
  onClose,
  sticker,
  nextSortOrder,
}: StickerModalProps) {
  const createSticker = useCreateFeedSticker();
  const updateSticker = useUpdateFeedSticker();

  const form = useForm<StickerFormData>({
    resolver: zodResolver(stickerFormSchema),
    defaultValues: { name: "", blobUrl: "", sortOrder: 0, isActive: true },
  });

  useEffect(() => {
    if (open) {
      form.reset({
        name: sticker?.name ?? "",
        blobUrl: sticker?.blobUrl ?? "",
        sortOrder: sticker?.sortOrder ?? nextSortOrder,
        isActive: sticker?.isActive ?? true,
      });
    }
  }, [open, sticker, nextSortOrder, form]);

  const onSubmit = async (data: StickerFormData) => {
    const payload: FeedStickerRequest = {
      name: data.name,
      blobUrl: data.blobUrl,
      sortOrder: data.sortOrder,
      isActive: data.isActive,
    };
    try {
      if (sticker?.id) {
        await updateSticker.mutateAsync({ id: sticker.id, data: payload });
      } else {
        await createSticker.mutateAsync(payload);
      }
      onClose();
    } catch {
      // Errors handled by hooks.
    }
  };

  const isSaving = createSticker.isPending || updateSticker.isPending;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{sticker ? "Edit Sticker" : "Create Sticker"}</DialogTitle>
          <DialogDescription>
            {sticker
              ? "Update this feed sticker's name, image, or order."
              : "Add a new sticker employees can use in the social feed."}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g. Thumbs Up" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="blobUrl"
              render={({ field, fieldState }) => (
                <FormItem>
                  <FormControl>
                    <ImageUploadField
                      label="Sticker Image"
                      id="sticker-image"
                      uploadUrlEndpoint={STICKER_UPLOAD_ENDPOINT}
                      value={field.value}
                      onChange={field.onChange}
                      error={fieldState.error?.message}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="sortOrder"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Sort Order</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      {...field}
                      onChange={(e) => field.onChange(Number(e.target.value))}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="isActive"
              render={({ field }) => (
                <FormItem className="flex items-center justify-between rounded-lg border p-3">
                  <div className="space-y-0.5">
                    <FormLabel>Active</FormLabel>
                    <p className="text-sm text-muted-foreground">
                      Inactive stickers are hidden from the employee feed.
                    </p>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSaving}>
                {sticker ? "Update" : "Create"} Sticker
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

function StickersContent() {
  const { data: stickers = [], isLoading, error } = useFeedStickers();
  const updateSticker = useUpdateFeedSticker();
  const deleteSticker = useDeleteFeedSticker();

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<FeedStickerResponse | undefined>();

  const sorted = useMemo(
    () =>
      [...stickers].sort(
        (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0),
      ),
    [stickers],
  );

  const nextSortOrder = useMemo(
    () =>
      stickers.reduce((max, s) => Math.max(max, s.sortOrder ?? 0), 0) + 1,
    [stickers],
  );

  const toPayload = (s: FeedStickerResponse): FeedStickerRequest => ({
    name: s.name ?? "",
    blobUrl: s.blobUrl ?? "",
    sortOrder: s.sortOrder ?? 0,
    isActive: s.isActive ?? true,
  });

  const handleToggleActive = (s: FeedStickerResponse) => {
    if (!s.id) return;
    updateSticker.mutate({
      id: s.id,
      data: { ...toPayload(s), isActive: !s.isActive },
    });
  };

  const handleReorder = (index: number, direction: -1 | 1) => {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= sorted.length) return;

    // Swap by array position, then reassign contiguous sortOrder values from the
    // resulting order. This is robust when items share the same stored sortOrder
    // (e.g. both 0): swapping stored values would be a no-op, but reassigning
    // contiguous indices always changes the persisted order.
    const reordered = [...sorted];
    [reordered[index], reordered[targetIndex]] = [
      reordered[targetIndex],
      reordered[index],
    ];

    reordered.forEach((item, position) => {
      if (!item.id) return;
      if ((item.sortOrder ?? 0) === position) return; // unchanged, skip the PUT
      updateSticker.mutate({
        id: item.id,
        data: { ...toPayload(item), sortOrder: position },
      });
    });
  };

  const handleEdit = (s: FeedStickerResponse) => {
    setEditing(s);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditing(undefined);
  };

  return (
    <div className="flex flex-col h-full">
      <Header
        title="Feed Stickers"
        description="Manage the sticker catalog available in the employee social feed"
      >
        <Button onClick={() => setModalOpen(true)} data-testid="add-sticker">
          <Plus className="w-4 h-4 mr-2" />
          Add Sticker
        </Button>
      </Header>

      <div className="flex-1 overflow-auto p-6 space-y-6">
        {error ? (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Error loading stickers</AlertTitle>
            <AlertDescription>Failed to load the sticker catalog.</AlertDescription>
          </Alert>
        ) : isLoading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {[...Array(8)].map((_, i) => (
              <Skeleton key={i} className="h-48 w-full rounded-lg" />
            ))}
          </div>
        ) : sorted.length === 0 ? (
          <div className="p-12 text-center text-muted-foreground">
            No stickers created yet.
          </div>
        ) : (
          <div
            className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4"
            data-testid="sticker-grid"
          >
            {sorted.map((sticker, index) => (
              <Card
                key={sticker.id}
                className={sticker.isActive ? "" : "opacity-60"}
                data-testid="sticker-card"
              >
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-center justify-center h-24 bg-muted/40 rounded-md">
                    {sticker.blobUrl ? (
                      <img
                        src={sticker.blobUrl}
                        alt={sticker.name ?? "Sticker"}
                        className="max-h-20 object-contain"
                      />
                    ) : (
                      <ImageOff className="h-8 w-8 text-muted-foreground" />
                    )}
                  </div>

                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium truncate">{sticker.name}</span>
                    <Badge variant={sticker.isActive ? "default" : "secondary"}>
                      {sticker.isActive ? "Active" : "Inactive"}
                    </Badge>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        disabled={index === 0 || updateSticker.isPending}
                        onClick={() => handleReorder(index, -1)}
                        aria-label="Move up"
                      >
                        <ArrowUp className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        disabled={
                          index === sorted.length - 1 || updateSticker.isPending
                        }
                        onClick={() => handleReorder(index, 1)}
                        aria-label="Move down"
                      >
                        <ArrowDown className="h-4 w-4" />
                      </Button>
                    </div>

                    <div className="flex items-center gap-1">
                      <Switch
                        checked={!!sticker.isActive}
                        onCheckedChange={() => handleToggleActive(sticker)}
                        disabled={updateSticker.isPending}
                        aria-label={`Toggle ${sticker.name}`}
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => handleEdit(sticker)}
                        aria-label="Edit"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            aria-label="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Sticker</AlertDialogTitle>
                            <AlertDialogDescription>
                              Are you sure you want to delete "{sticker.name}"?
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() =>
                                sticker.id && deleteSticker.mutate(sticker.id)
                              }
                            >
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      <StickerModal
        open={modalOpen}
        onClose={closeModal}
        sticker={editing}
        nextSortOrder={nextSortOrder}
      />
    </div>
  );
}

export default function StickersPage() {
  return (
    <FeedAdminGuard>
      <StickersContent />
    </FeedAdminGuard>
  );
}
